/* The Drop — storefront behaviour. Drawer-first cart, mega menu with intent
   delay, variant picker. No framework; the page works without any of it. */

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

/* ── Drawers (mobile menu, cart) ─────────────────────────────────── */
const overlay = $('[data-overlay]');
let lastFocus = null;

function openDrawer(id, { focus = true } = {}) {
  const drawer = document.getElementById(id);
  if (!drawer) return;
  if (focus) lastFocus = document.activeElement;
  drawer.classList.add('is-open');
  drawer.removeAttribute('inert');
  overlay?.classList.add('is-open');
  document.body.classList.add('is-locked');
  if (focus) ($('a, button, input', drawer) || drawer).focus();
}

function closeDrawers() {
  $$('.drawer.is-open').forEach((d) => {
    d.classList.remove('is-open');
    d.setAttribute('inert', '');
  });
  overlay?.classList.remove('is-open');
  document.body.classList.remove('is-locked');
  lastFocus?.focus();
}

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (opener) {
    e.preventDefault();
    openDrawer(opener.dataset.drawerOpen);
    return;
  }
  if (e.target.closest('[data-drawer-close]') || e.target.matches('[data-overlay]')) closeDrawers();
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeDrawers();
});

/* ── Mega menu — opens after 120ms of intent, closes instantly ───── */
$$('.has-mega').forEach((item) => {
  let timer;
  item.addEventListener('mouseenter', () => {
    timer = setTimeout(() => item.classList.add('is-open'), 120);
  });
  item.addEventListener('mouseleave', () => {
    clearTimeout(timer);
    item.classList.remove('is-open');
  });
});

/* ── Cart ────────────────────────────────────────────────────────── */
async function refreshCart({ open = false } = {}) {
  const [sections, cart] = await Promise.all([
    fetch('/?sections=cart-drawer').then((r) => r.json()),
    fetch('/cart.js').then((r) => r.json()),
  ]);

  const holder = $('#shopify-section-cart-drawer');
  // The .drawer lives inside this section, so replacing it throws away the open
  // state and strands the overlay + body lock over a drawer that no longer exists.
  const wasOpen = !!holder?.querySelector('.drawer.is-open');
  if (holder && sections['cart-drawer']) holder.innerHTML = sections['cart-drawer'];

  $$('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count;
    el.closest('[data-cart-count-wrap]')?.toggleAttribute('hidden', cart.item_count === 0);
  });

  if (open || wasOpen) openDrawer('cart-drawer', { focus: open });
}

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-add-to-cart]');
  if (!form) return;
  e.preventDefault();

  const button = $('[type="submit"]', form);
  const label = button?.textContent;
  if (button) {
    button.disabled = true;
    button.textContent = window.themeStrings?.adding || 'Adding…';
  }

  const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });

  if (button) {
    button.disabled = false;
    button.textContent = label;
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const slot = $('[data-cart-error]', form);
    if (slot) slot.textContent = err.description || window.themeStrings?.addError || 'That could not be added.';
    return;
  }

  await refreshCart({ open: true });
});

// Quantity steppers and line removal, in the drawer and on the cart page.
document.addEventListener('click', async (e) => {
  const step = e.target.closest('[data-qty-step]');
  if (step) {
    const input = $('input', step.parentElement);
    const next = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const remove = e.target.closest('[data-cart-remove]');
  if (remove) {
    e.preventDefault();
    await changeLine(remove.dataset.cartRemove, 0);
  }
});

document.addEventListener('change', async (e) => {
  const input = e.target.closest('[data-cart-qty]');
  if (input) await changeLine(input.dataset.cartQty, Number(input.value));
});

async function changeLine(key, quantity) {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });
  if ($('[data-cart-page]')) {
    window.location.reload();
  } else {
    await refreshCart();
  }
}

/* ── Variant picker ──────────────────────────────────────────────── */
$$('[data-product-form]').forEach((form) => {
  const data = JSON.parse($('[data-variants]', form)?.textContent || 'null');
  if (!data) return;

  const idInput = $('[name="id"]', form);
  const button = $('[data-add-button]', form);
  const priceSlot = $('[data-price-display]');
  const soldOut = $('[data-sold-out]');

  const selected = () => $$('input[data-option]:checked', form).map((i) => i.value);

  form.addEventListener('change', (e) => {
    if (!e.target.matches('input[data-option]')) return;

    const chosen = selected();
    const variant = data.variants.find((v) => v.options.every((o, i) => o === chosen[i]));

    if (!variant) return;
    idInput.value = variant.id;
    if (priceSlot) priceSlot.textContent = variant.price;

    const available = variant.available;
    button.hidden = !available;
    button.disabled = !available;
    if (soldOut) soldOut.hidden = available;
    if (available && button.dataset.labelTemplate) {
      button.textContent = button.dataset.labelTemplate.replace('%price%', variant.price);
    }

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  });
});

/* ── Product gallery — snap track, thumbs drive it ───────────────── */
$$('[data-gallery]').forEach((gal) => {
  const track = $('[data-gallery-track]', gal);
  const thumbs = $$('[data-gallery-go]', gal);
  const counter = $('[data-gallery-current]', gal);
  if (!track || !thumbs.length) return;

  const mark = (i) => {
    if (counter) counter.textContent = i + 1;
    thumbs.forEach((b, k) => {
      if (k === i) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  };

  // Instant jump, and mark directly: smooth scrollTo on a mandatory-snap
  // container is cancelled by the snap re-anchor in Chromium, and scroll
  // events are throttled in background tabs. Swipes stay smooth natively.
  thumbs.forEach((b) =>
    b.addEventListener('click', () => {
      const i = Number(b.dataset.galleryGo);
      track.scrollTo({ left: i * track.clientWidth });
      mark(i);
    })
  );

  let raf;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => mark(Math.round(track.scrollLeft / track.clientWidth)));
  });
});

/* ── Sticky buy bar — appears only once the real button is gone ───── */
(() => {
  const bar = $('[data-buybar]');
  const mainBtn = $('[data-add-button]');
  const form = $('form[data-add-to-cart]');
  if (!bar || !mainBtn || !form) return;

  new IntersectionObserver(
    ([entry]) => { bar.hidden = entry.isIntersecting; },
    { rootMargin: '0px 0px -80px 0px' }
  ).observe(mainBtn);

  // Submit the real form so variant, quantity and the ajax cart all behave.
  $('[data-buybar-add]', bar)?.addEventListener('click', () => form.requestSubmit());
})();

/* ── Collection sort submits itself ──────────────────────────────── */
$$('[data-auto-submit]').forEach((el) => {
  el.addEventListener('change', () => el.closest('form').submit());
});
